// src/components/SearchHeader.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPhoneNumber } from '../utils/customerHelpers';
import { 
  Search, 
  X, 
  Users, 
  Package, 
  MessageSquare, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Package2,
  Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import useGlobalSearch from '../hooks/useGlobalSearch';

const SearchHeader = ({ 
  variant = "navbar", 
  className = "",
  position = "navigation" // navigation, sidebar, standalone
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  
  // Use the custom hook for all search functionality
  const {
    query,
    showResults,
    loading,
    searchResults,
    debouncedQuery,
    handleInputChange,
    handleResultClick: clearSearch,
    handleKeyDown,
    handleFocus,
    handleBlur,
    getCustomerDisplayName,
    getCustomerEmail,
    getOrderDisplayName,
    getOrderCompany,
    getOrderStatus,
    getQuoteDisplayName,
    getQuoteDescription,
    getQuoteStatus,
    formatDate,
    isSearching,
    totalDataCount
  } = useGlobalSearch();

  // Handle click outside to collapse
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    handleInputChange('');
    clearSearch();
  };

  const handleResultClick = (type, item) => {
    clearSearch();
    setIsExpanded(false);
    
    // Navigate to the appropriate page
    switch (type) {
      case 'customer':
        navigate('/customers');
        break;
      case 'order':
        navigate('/orders');
        break;
      case 'quote':
        navigate('/quotes');
        break;
      default:
        break;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Escape') {
      handleCollapse();
    }
    handleKeyDown(e.key);
  };

  const getStatusVariant = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'delivered':
        return 'default';
      case 'pending':
      case 'in progress':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const isDesktop = variant === "navbar";
  const isMobile = variant === "mobile";
  const isStandalone = variant === "standalone";

  return (
    <div 
      ref={searchRef}
      className={cn(
        "relative flex items-center",
        position === "navigation" && "ml-4",
        position === "sidebar" && "w-full",
        className
      )}
    >
      {/* Collapsed State - Just Search Icon */}
      {!isExpanded && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExpand}
          className={cn(
            "h-9 w-9 p-0 transition-colors",
            position === "navigation" && "text-white hover:text-white hover:bg-white/20 border border-white/30",
            position === "sidebar" && "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          )}
          aria-label="Search"
        >
          <Search className="h-5 w-5" 
            style={variant === "navbar" ? { color: "#ed1d35" } : {}} 
          />
        </Button>
      )}

      {/* Expanded State - Full Search Bar */}
      {isExpanded && (
        <div className={cn(
          "absolute z-50 bg-white rounded-lg shadow-lg border",
          position === "navigation" && "right-0 top-full mt-2 w-72",
          position === "sidebar" && "left-0 top-full mt-2 w-full",
          position === "standalone" && "relative w-full"
        )}>
          <div className="p-2">
                        {/* Search Input */}
            <div className="relative">
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyPress}
                className={cn(
                  "pl-3 pr-8 h-8 text-sm text-black",
                  variant === "navbar"
                    ? "border-[#ed1d35] focus:border-[#ed1d35] focus:ring-[#ed1d35]"
                    : "border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                )}
                aria-label="Global Search"
              />
          
                              {/* Loading Spinner */}
                {(isSearching || loading) && (
                  <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                  </div>
                )}
                
                {/* Clear/Close Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={query.length > 0 ? () => handleInputChange('') : handleCollapse}
                  className="absolute right-0.5 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0 hover:bg-slate-100"
                  aria-label={query.length > 0 ? "Clear search" : "Close search"}
            >
                  <X className="h-3 w-3" />
                </Button>
            </div>

            {/* Search Results */}
        {showResults && query.length > 0 && (
                              <Card className="mt-2 border-slate-200 max-h-80 overflow-y-auto">
                <CardContent className="p-0">
            {searchResults.total > 0 ? (
                    <div className="divide-y divide-slate-100">
                                              {/* Search Info */}
                        <div className="p-2 text-xs text-slate-600 bg-slate-50">
                          Found <span className="font-semibold">{searchResults.total}</span> result{searchResults.total !== 1 ? 's' : ''} for "{debouncedQuery}"
                  </div>

                  {/* Customers */}
                  {searchResults.customers.length > 0 && (
                        <div>
                          <div className="p-2 bg-slate-50 border-b border-slate-100">
                            <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                              <Users className="h-3 w-3" />
                        Customers ({searchResults.customers.length})
                            </div>
                          </div>
                      {searchResults.customers.map((customer, index) => (
                                                          <div
                          key={`customer-${index}`}
                          onClick={() => handleResultClick('customer', customer)}
                                className="p-2 hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                                                              <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-slate-900">{getCustomerDisplayName(customer)}</div>
                                    <div className="text-xs text-slate-500 mt-1">
                                {getCustomerEmail(customer)}
                                {customer.phone && (
                                  <>
                                    <span className="mx-1">•</span>
                                    {formatPhoneNumber(customer.phone)}
                                  </>
                                )}
                              </div>
                              {customer.address && (
                                      <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                  {customer.address}
                                </div>
                              )}
                            </div>
                                  <Users className="h-3 w-3 text-slate-400 flex-shrink-0 ml-3" />
                                </div>
                            </div>
                          ))}
                          </div>
                  )}

                  {/* Orders */}
                  {searchResults.orders.length > 0 && (
                        <div>
                          <div className="p-3 bg-slate-50 border-b border-slate-100">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                              <Package className="h-4 w-4" />
                        Orders ({searchResults.orders.length})
                            </div>
                          </div>
                      {searchResults.orders.map((order, index) => (
                            <div
                          key={`order-${index}`}
                          onClick={() => handleResultClick('order', order)}
                              className="p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-medium text-slate-900">{getOrderDisplayName(order)}</div>
                                  <div className="text-sm text-slate-500 mt-1">
                                {getOrderCompany(order)}
                                {order.deliveryDate && (
                                  <>
                                    <span className="mx-1">•</span>
                                        <Calendar className="h-3 w-3 inline mr-1" />
                                    Due {formatDate(order.deliveryDate)}
                                  </>
                                )}
                              </div>
                              {order.total && (
                                    <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                      <DollarSign className="h-3 w-3" />
                                  {new Intl.NumberFormat('en-US', { 
                                    style: 'currency', 
                                    currency: 'USD' 
                                  }).format(order.total)}
                                </div>
                              )}
                            </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                  <Badge variant={getStatusVariant(getOrderStatus(order).status)}>
                                {getOrderStatus(order).status}
                                  </Badge>
                                  <Package className="h-4 w-4 text-slate-400" />
                                </div>
                              </div>
                            </div>
                          ))}
                          </div>
                  )}

                  {/* Quotes */}
                  {searchResults.quotes.length > 0 && (
                        <div>
                          <div className="p-3 bg-slate-50 border-b border-slate-100">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                              <MessageSquare className="h-4 w-4" />
                        Quotes ({searchResults.quotes.length})
                            </div>
                          </div>
                      {searchResults.quotes.map((quote, index) => (
                            <div
                          key={`quote-${index}`}
                          onClick={() => handleResultClick('quote', quote)}
                              className="p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-medium text-slate-900">{getQuoteDisplayName(quote)}</div>
                                  <div className="text-sm text-slate-500 mt-1">
                                {getQuoteDescription(quote)}
                                {quote.deliveryDate && (
                                  <>
                                    <span className="mx-1">•</span>
                                        <Calendar className="h-3 w-3 inline mr-1" />
                                    Due {formatDate(quote.deliveryDate)}
                                  </>
                                )}
                              </div>
                              {(quote.project_details?.quantity || quote.quantity) && (
                                    <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                      <Package2 className="h-3 w-3" />
                                  Qty: {quote.project_details?.quantity || quote.quantity}
                                </div>
                              )}
                            </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                  <Badge variant={getStatusVariant(getQuoteStatus(quote).status)}>
                                {getQuoteStatus(quote).status}
                                  </Badge>
                                  <MessageSquare className="h-4 w-4 text-slate-400" />
                                </div>
                              </div>
                            </div>
                          ))}
                          </div>
                      )}
                    </div>
            ) : (
                    <div className="p-6 text-center">
                      <Search className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <div className="text-slate-500">No results found for "{debouncedQuery}"</div>
                      <div className="text-sm text-slate-400 mt-1">
                  {loading ? 'Loading data...' : `Searched ${totalDataCount} items`}
                </div>
                    </div>
            )}
                </CardContent>
          </Card>
        )}
      </div>
        </div>
      )}
    </div>
  );
};

export default SearchHeader; 